import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Play, Pause, Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const trackPlayback = (event, payload) => {
  // Fire-and-forget; silent on failure. Telemetry must never break playback.
  try {
    axios
      .post(
        `${API}/analytics/track-playback`,
        { event, ...payload },
        { withCredentials: true }
      )
      .catch(() => {});
  } catch {
    /* noop */
  }
};

const VoicePlaybackButton = ({
  text,
  language = 'pl',
  fetcher,
  compact = false,
  autoplay = false,
  autoplayStamp = 0,
  messageId,
  conversationId,
  inputTypeOrigin,
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState('idle'); // idle | loading | playing | paused | error
  const audioRef = useRef(null);
  const objectUrlRef = useRef(null);
  const lastAutoplayStampRef = useRef(null);

  // Clean up object URL on unmount
  useEffect(
    () => () => {
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch { /* noop */ }
        audioRef.current = null;
      }
      if (objectUrlRef.current) {
        try { URL.revokeObjectURL(objectUrlRef.current); } catch { /* noop */ }
        objectUrlRef.current = null;
      }
    },
    []
  );

  const ensureAudio = useCallback(async () => {
    if (audioRef.current) return audioRef.current;
    setState('loading');
    try {
      const blob = await fetcher(text, language);
      if (!blob || blob.size === 0) {
        setState('error');
        return null;
      }
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      const audio = new Audio(url);
      audio.onended = () => {
        setState('idle');
        if (messageId) {
          trackPlayback('play_completed', {
            message_id: messageId,
            conversation_id: conversationId,
            tts_language: language,
            input_type_origin: inputTypeOrigin,
          });
        }
      };
      audio.onpause = () => {
        if (audio.currentTime < audio.duration) setState('paused');
      };
      audio.onplay = () => {
        setState('playing');
        if (messageId) {
          trackPlayback('play_started', {
            message_id: messageId,
            conversation_id: conversationId,
            tts_language: language,
            input_type_origin: inputTypeOrigin,
          });
        }
      };
      audio.onerror = () => {
        setState('error');
        if (messageId) {
          trackPlayback('play_failed', {
            message_id: messageId,
            conversation_id: conversationId,
            tts_language: language,
            input_type_origin: inputTypeOrigin,
          });
        }
      };
      audioRef.current = audio;
      return audio;
    } catch {
      setState('error');
      if (messageId) {
        trackPlayback('play_failed', {
          message_id: messageId,
          conversation_id: conversationId,
          tts_language: language,
          input_type_origin: inputTypeOrigin,
        });
      }
      return null;
    }
  }, [text, language, fetcher, messageId, conversationId, inputTypeOrigin]);

  const handleClick = useCallback(async () => {
    try {
      if (state === 'playing') {
        if (audioRef.current) audioRef.current.pause();
        return;
      }
      const audio = await ensureAudio();
      if (!audio) return;
      await audio.play();
    } catch {
      // Browser blocked playback or audio failed — surface "retry"
      setState('error');
    }
  }, [state, ensureAudio]);

  // Autoplay effect — fires once when autoplay flips true, or when stamp changes.
  useEffect(() => {
    if (!autoplay) return;
    if (lastAutoplayStampRef.current === autoplayStamp) return;
    lastAutoplayStampRef.current = autoplayStamp;

    const run = async () => {
      try {
        const audio = await ensureAudio();
        if (!audio) return;
        await audio.play();
      } catch {
        // Mobile autoplay block, decode error, etc. — silently fall back to manual play.
        setState('idle');
      }
    };
    // Fire-and-forget; explicitly attach catch so an async rejection never
    // bubbles up to window.onerror (which would trigger the CRA dev overlay).
    run().catch(() => setState('idle'));
  }, [autoplay, autoplayStamp, ensureAudio]);

  let icon;
  let label;
  if (state === 'loading') {
    icon = <Loader2 className="w-4 h-4 animate-spin" />;
    label = t('voice.loading');
  } else if (state === 'playing') {
    icon = <Pause className="w-4 h-4" />;
    label = t('voice.pause');
  } else if (state === 'error') {
    icon = <AlertCircle className="w-4 h-4" />;
    label = t('voice.retry');
  } else {
    icon = <Play className="w-4 h-4" />;
    label = t('voice.play');
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      className={`hussar-tts-btn ${compact ? 'hussar-tts-btn-compact' : ''}`}
      data-testid="voice-playback-btn"
      title={label}
    >
      {icon}
      {!compact && <span>{label}</span>}
    </Button>
  );
};

export default VoicePlaybackButton;
